import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Pill from 'srcReact/components/common2/Pill'
import { checkNested } from 'srcReact/helpers/validator'
import HistoryTree from '../../../HistoryTree'
import PrivateDataMessage from '../PrivateDataMessage'
import PromptBlock from '../../../PromptBlock'
import styles from './styles.module.scss'

export default function InfoBlock() {
  const { t } = useTranslation()

  const [commonInterestCount, setCommonInterestCount] = useState(0)

  const {
    myProfile: {
      profileData: { user },
      profileData,
      userEducation,
      userInterests,
      userExperience,
      userExpertise,
      userStatus
    },
    login: { user: loggedInUser }
  } = useSelector((state) => state)

  useEffect(() => {
    const commonInterest = userInterests.list.filter((int) => int.is_common_with_logged_user === 1)
    setCommonInterestCount(commonInterest.length)
  }, [userInterests.list])

  const isDataPrivate = () => {
    const { user } = profileData
    if (user) {
      if (user.id === loggedInUser.id) {
        return false
      }
      const {
        is_display_ci,
        education_1,
        education_2,
        education_3,
        experiences,
        favorite_sport_team,
        favorite_color,
        languages,
        originally_from,
        city,
        children,
        about_me
      } = user
      if (
        is_display_ci == 1 ||
        children ||
        city ||
        (experiences && experiences.experiences && experiences.experiences.length > 0) ||
        favorite_color ||
        favorite_sport_team ||
        (languages && languages.length > 0) ||
        originally_from ||
        education_1 ||
        education_2 ||
        education_3 ||
        about_me
      ) {
        return false
      }
    }
    return true
  }

  return isDataPrivate() ? (
    <PrivateDataMessage name={profileData.user.first_name} />
  ) : userStatus === 'deleted' ? (
    ''
  ) : (
    <div className={`${styles.infoContainer}`}>
      {profileData.user.about_me ? (
        <React.Fragment>
          <h2 className={`${styles.aboutTitle}`}>
            {t('my_profile.info_block.about')} {profileData.user.first_name}
          </h2>
          <h2 className={`${styles.aboutDesc}`}>{profileData.user.about_me}</h2>
        </React.Fragment>
      ) : (
        ''
      )}
      {profileData.user.is_display_ci === '1' ? (
        <React.Fragment>
          <h2 className={`${styles.sectionTitle}`}>
            {t('my_profile.info_block.interests', { username: profileData.user.first_name })}
          </h2>
          {commonInterestCount > 0 && (
            <h2 className={`${styles.interestsMatch}`}>
              {t('my_profile.info_block.commonInterestsWithStat', {
                username: profileData.user.first_name,
                count: commonInterestCount,
                total: userInterests.list.length
              })}
            </h2>
          )}

          <div className={`${styles.pillWrapper}`}>
            {userInterests?.list?.map((int) => (
              <Pill
                dataId={int.id}
                text={int.name}
                shared={int.is_common_with_logged_user === 1}
                selected={true}
                askForAddToProfile={int.is_common_with_logged_user === 0}
              />
            ))}
          </div>
        </React.Fragment>
      ) : (
        ''
      )}

      {userExpertise.list?.length > 0 && (
        <React.Fragment>
          <h2 className={`${styles.sectionTitle}`}>
            {t('my_profile.info_block.expertise', { username: profileData.user.first_name })}
          </h2>
          <div className={`${styles.pillWrapper} ${styles.expertisePillWrapper}`}>
            {userExpertise.list.map((i) => (
              <Pill type='expertise' shared={i.is_common_with_logged_user === 1} selected={true} text={i.name} />
            ))}
          </div>
        </React.Fragment>
      )}

      {user?.prompts?.prompts?.length > 0 && (
        <PromptBlock
          user={user}
          title={t('my_profile.info_block.prompt', { username: profileData.user.first_name })}
          block='userProfile'
        />
      )}

      {userExperience.list && userExperience.list.length > 0 && (
        <React.Fragment>
          <h2 className={`${styles.sectionTitle}`}>
            {t('my_profile.info_block.journey', { username: profileData.user.first_name })}
          </h2>
          <HistoryTree treeType={'experiences'} expData={userExperience.list} />
        </React.Fragment>
      )}

      {userEducation.list && userEducation.list.length > 0 && (
        <React.Fragment>
          <h2 className={`${styles.sectionTitle}`}>
            {t('my_profile.info_block.education', { username: profileData.user.first_name })}
          </h2>
          <HistoryTree treeType={'education'} expData={userEducation.list} />
        </React.Fragment>
      )}

      {profileData.user.languages.length > 0 ? (
        <React.Fragment>
          <h2 className={`${styles.sectionTitle}`}>
            {t('my_profile.info_block.languagesSpeaks', { username: profileData.user.first_name })}
          </h2>
          <div className={`${styles.pillWrapper}`}>
            {profileData.user && profileData.user.languages.map((i) => <Pill text={i.name} />)}
          </div>
        </React.Fragment>
      ) : (
        ''
      )}

      {profileData.user.children ? (
        <h2 className={`${styles.sectionTitle}`}>
          {t('my_profile.info_block.parent', {
            username: profileData.user.first_name,
            count: parseInt(profileData.user.children)
          })}
        </h2>
      ) : (
        ''
      )}
      {profileData.user.favorite_color ? (
        <React.Fragment>
          <h2 className={`${styles.sectionTitle}`}>
            {t('my_profile.info_block.color', { username: profileData.user.first_name })}
          </h2>
          <h2 className={`${styles.subDesc}`}>{profileData.user.favorite_color}</h2>
        </React.Fragment>
      ) : (
        ''
      )}
      {profileData.user.originally_from ? (
        <React.Fragment>
          <h2 className={`${styles.sectionTitle}`}>
            {t('my_profile.info_block.native', { username: profileData.user.first_name })}
          </h2>
          <h2 className={`${styles.subDesc}`}>{profileData.user.originally_from}</h2>
        </React.Fragment>
      ) : (
        ''
      )}
      {profileData.user.city ? (
        <React.Fragment>
          <h2 className={`${styles.sectionTitle}`}>
            {t('my_profile.info_block.city', { username: profileData.user.first_name })}
          </h2>
          <h2 className={`${styles.subDesc}`}>{profileData.user.city}</h2>
        </React.Fragment>
      ) : (
        ''
      )}
      {profileData.user.favorite_sport_team ? (
        <React.Fragment>
          <h2 className={`${styles.sectionTitle}`}>
            {t('my_profile.info_block.sport_team', { username: profileData.user.first_name })}
          </h2>
          <h2 className={`${styles.subDesc}`}>{profileData.user.favorite_sport_team}</h2>
        </React.Fragment>
      ) : (
        ''
      )}
    </div>
  )
}
